import type { RecommendationContextAxis } from "@memorang/types";
import type {
	EffectiveMastery,
	FullCell,
} from "../contract/recommendation-context-types";

const chainKey = (
	cell: FullCell,
	progressionAxisId: string,
	axes: RecommendationContextAxis[],
): string =>
	axes
		.filter(({ id }) => id !== progressionAxisId)
		.map(({ id }) => `${id}:${cell.coordinates[id]}`)
		.join("|");

export const observedAsEffective = (cell: FullCell): EffectiveMastery => ({
	...cell.mastery,
	inferred: false,
	inferredFrom: null,
});

export const inferMastery = (
	cells: FullCell[],
	progressionAxis: RecommendationContextAxis,
	axes: RecommendationContextAxis[],
): Map<string, EffectiveMastery> => {
	const order = new Map(
		[...progressionAxis.values]
			.sort(
				(left, right) => left.sortOrder - right.sortOrder || left.id - right.id,
			)
			.map(({ id }, index) => [id, index]),
	);
	const chains = new Map<string, FullCell[]>();
	for (const cell of cells) {
		const key = chainKey(cell, progressionAxis.id, axes);
		chains.set(key, [...(chains.get(key) ?? []), cell]);
	}
	const effective = new Map<string, EffectiveMastery>();
	for (const chain of chains.values()) {
		chain.sort(
			(left, right) =>
				(order.get(right.coordinates[progressionAxis.id]) ?? -1) -
				(order.get(left.coordinates[progressionAxis.id]) ?? -1),
		);
		// Traversing from highest to lowest guarantees that this source is always
		// at the current or a higher progression value. A lower value can never
		// influence a higher one.
		let strongestHigherOrCurrent: FullCell | null = null;
		for (const cell of chain) {
			// Only a cell carrying enough distinct evidence may act as a source. A
			// thin probe that happens to score high must not dictate the standing of
			// the values beneath it: the whole mastery record travels to the target,
			// so an unproven source would otherwise overwrite settled results below.
			if (
				cell.mastery.value !== null &&
				cell.mastery.evidenceStatus === "ELIGIBLE" &&
				(strongestHigherOrCurrent === null ||
					cell.mastery.value > (strongestHigherOrCurrent.mastery.value ?? -1))
			) {
				strongestHigherOrCurrent = cell;
			}
			const source = strongestHigherOrCurrent ?? cell;
			effective.set(cell.source.key, {
				...source.mastery,
				inferred: source.source.key !== cell.source.key,
				inferredFrom:
					source.source.key === cell.source.key
						? null
						: {
								axisId: progressionAxis.id,
								valueId: source.coordinates[progressionAxis.id],
							},
			});
		}
	}
	return effective;
};
